import React, { useEffect, useState } from "react";
import {
  Container,
  Typography,
  Box,
  CircularProgress,
  Button,
  Paper,
  Alert,
  Snackbar,
  Fab,
} from "@mui/material";
import AddIcon from '@mui/icons-material/Add';
import { useDispatch, useSelector } from "react-redux";
import { fetchUserData } from '../store/actions/authActions';
import { fetchUserAccounts } from "../store/actions/accountActions";
import AccountSetupWizard from '../components/AccountSetup/AccountSetupWizard';
import AccountSelector from '../components/AccountSelector';
import { useLocation } from 'react-router-dom';

function Dashboard() {
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  const { accounts, isLoading, error } = useSelector((state) => state.account);
  const [isSetupOpen, setIsSetupOpen] = useState(false);
  const location = useLocation();
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    dispatch(fetchUserData());
    dispatch(fetchUserAccounts());
  }, [dispatch]);

  useEffect(() => {
    if (location.state?.message) {
      setSuccessMessage(location.state.message);
      window.history.replaceState({}, document.title);
    }
  }, [location]);

  const handleCreateAccount = () => {
    setIsSetupOpen(true);
  };

  if (isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "60vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <Snackbar
        open={!!successMessage}
        autoHideDuration={6000}
        onClose={() => setSuccessMessage('')}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setSuccessMessage('')} 
          severity="success"
          variant="filled"
        >
          {successMessage}
        </Alert>
      </Snackbar>

      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 4 
        }}>
          <Typography variant="h4">
            Dashboard
          </Typography>
          {accounts.length > 0 && (
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={handleCreateAccount}
            >
              Create New Account
            </Button>
          )}
        </Box>

        {error && (
          <Typography color="error" sx={{ mb: 2 }}>
            {error}
          </Typography>
        )}

        <Box sx={{ mb: 4 }}>
          {user ? (
            <Typography variant="h6" gutterBottom>
              Welcome, {user.name}!
            </Typography>
          ) : (
            <CircularProgress size={20} sx={{ mr: 1 }} />
          )}
        </Box>

        {accounts.length === 0 ? (
          <Paper sx={{ p: 3, textAlign: "center" }}>
            <Typography variant="h6" gutterBottom>
              No Accounts Found
            </Typography>
            <Typography color="textSecondary" paragraph>
              You currently don't have any accounts set up.
            </Typography>
            <Button
              variant="contained"
              color="primary"
              onClick={handleCreateAccount}
            >
              Create New Account
            </Button>
          </Paper>
        ) : (
          <>
            <AccountSelector accounts={accounts} />

            {/* Optional: Floating Action Button for mobile */}
            <Box
              sx={{
                display: { md: 'none' },
                position: 'fixed',
                bottom: 16,
                right: 16,
              }}
            >
              <Fab
                color="primary"
                aria-label="add account"
                onClick={handleCreateAccount}
              >
                <AddIcon />
              </Fab>
            </Box>
          </>
        )}
      </Container>
      
      <AccountSetupWizard 
        open={isSetupOpen} 
        onClose={() => setIsSetupOpen(false)} 
      />
    </>
  );
}

export default Dashboard; 